import React from 'react';
import CustomModal from '../../baseComponents/gui/customModal/CustomModal';
import CustomButton from '../../features/customButton/ui/main/CustomButton';
import classNames from 'classnames';
import Back from '../../features/back/Back';
import { safeHTML } from '../../../utils/safeHTML';
import { useContent } from '../../../redux/reducer/content';
import { useModal } from '../../../hooks/useModal';
import { useCountryControls } from '../../../hooks/useCountryControls';
import { useTranslation } from '../../../translations';
import { copyright, copyrightParams } from '../../../constants/copyright';
import { getCountryNames } from '../../../constants/country';
import { useUser } from '../../../redux/reducer/user';
import { useLanguageControls } from '../../../hooks/useLanguageControls';
import { useRequests } from '../../../redux/reducer/requests';

const SelectCountryModal = React.forwardRef(function SelectCountryModal(
  { title, description, onCountrySelected },
  ref,
) {
  const { state } = useContent();
  const { id, closeModal } = useModal();
  const { availableCountries, changeCountry, canChangeCountry } = useCountryControls();
  const { language } = useLanguageControls();
  const { profile } = useUser();
  const {
    authData: { isWeb },
  } = useRequests();
  const { t } = useTranslation();
  const countryNames = getCountryNames(t);
  const selectedCountry = profile?.userCountry;
  const currentLang = language;
  const currentCountryImage = currentLang ? `/images/change-country/change-country-${currentLang}.png` : '';
  const close = () => closeModal({ id });
  const isCountryChangeLocked = Boolean(selectedCountry) && !canChangeCountry;
  const hasMoreThanTwoCountries = availableCountries.length > 2;

  const handleCountrySelect = (country) => {
    if (!country || country === selectedCountry || isCountryChangeLocked) {
      return;
    }

    changeCountry(country);
    onCountrySelected?.(country);
    close();
  };

  return (
    <CustomModal isFullPage={true} animation={['fade']}>
      <div className={classNames(`select-country-modal select-country-modal_${state}`)} ref={ref}>
        <div className={classNames('select-country-modal__wrapper')}>
          <Back
            className={classNames('select-country-modal__back', {
              'select-country-modal__back_app': !isWeb,
            })}
            onClick={close}
          />
          {currentCountryImage && <img className='select-country-modal__img' src={currentCountryImage} alt='' />}
          <div className='select-country-modal__content'>
            <div
              className={classNames('select-country-modal__header', {
                'select-country-modal__header-smaller-margin': hasMoreThanTwoCountries,
              })}
            >
              <h2 className='select-country-modal__title'>{safeHTML(title)}</h2>
              <div className='select-country-modal__description'>{safeHTML(description)}</div>
            </div>
            <ul className='select-country-modal__buttons-list'>
              {availableCountries.map((country) => {
                const isSelectedCountry = selectedCountry === country;
                const isDisabled = isCountryChangeLocked && !isSelectedCountry;

                return (
                  <li key={country} className='select-country-modal__list-item'>
                    <CustomButton
                      className={classNames('select-country-modal__button', {
                        'select-country-modal__button_active': isSelectedCountry,
                        'select-country-modal__button_disabled': isDisabled,
                      })}
                      text={countryNames[country] ?? country.toUpperCase()}
                      onClick={() => handleCountrySelect(country)}
                      disabled={isDisabled}
                    />
                  </li>
                );
              })}
            </ul>
            <div className='select-country-modal__copyright'>{safeHTML(t(copyright, copyrightParams))}</div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
});

export default SelectCountryModal;
SelectCountryModal.propTypes = {};
